import React from 'react';
import LivePage from './LivePage';
import submenuTests from './menus/submenu-tests';
import BadgeColorHash from '../components/common/BadgeColorHash';
import SimilarityBadge from '../components/common/SimilarityBadge';
import SingleValueSelector from '../components/common/editors/SingleValueSelector';
import MultiClusterScatterModal from '../components/embedding-explorer/MultiClusterScatterModal';
import { clusterPairAdminUrl } from '../components/fusebox-benchmark/fuseboxBenchmarkUtils';
import LegoAdminPageContext from './legoAdminPageContext';
import { IconButton } from '../components/common/IconButton';
import ModelIdSelector from '../components/lego/ModelIdSelector';

// Numeric columns that get a (>,<)(0.N) filter box. `sim` ones render with SimilarityBadge.
const NUM_COLS = [
  { key: 'median', label: 'Median best', sim: true, title: 'Median over every image\'s best cross-cluster match' },
  { key: 'avg', label: 'Avg best', sim: true, title: 'Average over every image\'s best cross-cluster match' },
  { key: 'best', label: 'Best pair', sim: true, title: 'Closest cross-cluster image pair' },
  { key: 'worst', label: 'Worst pair', sim: true, title: 'Least-represented image\'s best counterpart' },
  { key: 'fisherJ', label: 'Fisher J', sim: false, title: '(μA−μB)² / (varA+varB) on the difference-of-means axis (in-sample)' },
  { key: 'auc', label: 'Held-out AUC', sim: false, title: 'Axis fit on half the images, scored on the other half (≈0.5 same, ≈1 distinct)' },
];

const POL_CLASS = { link: 'badge-success', reject: 'badge-danger', related: 'badge-info' };
const POL_RANK = { link: 0, related: 1, reject: 2 };
const POLARITY_OPTIONS = ['link', 'reject', 'related'];

// Every sortable column → the value to sort a row by (strings sort lexically, numbers numerically;
// nullish numbers sink). Mirrors the rendered columns: ids, polarity, image count, and the metrics.
const SORT_VALUE = {
  clusterIdA: (r) => r.clusterIdA,
  clusterIdB: (r) => r.clusterIdB,
  polarity: (r) => POL_RANK[r.polarity] ?? 9,
  imgs: (r) => r.nA + r.nB,
  ...NUM_COLS.reduce((acc, c) => ({ ...acc, [c.key]: (r) => (r[c.key] == null ? -Infinity : r[c.key]) }), {}),
};
const STRING_SORT_COLS = new Set(['clusterIdA', 'clusterIdB']);

// Filterable numeric columns → the value each row compares against. The metrics filter on their own
// field; the imgs filter ("min images per side") uses the smaller of the two cluster sizes.
const FILTER_VALUE = {
  ...NUM_COLS.reduce((acc, c) => ({ ...acc, [c.key]: (r) => r[c.key] }), {}),
  imgs: (r) => Math.min(r.nA, r.nB),
};
const FILTER_KEYS = Object.keys(FILTER_VALUE);

const emptyFilters = () => FILTER_KEYS.reduce((acc, k) => ({ ...acc, [k]: { op: '>', val: '' } }), {});

const ROW_LIMIT = 100;

/**
 * /test/fusebox-clusters-links — one row per image_cluster_links doc with the same metrics the
 * linkDiscriminabilityTable.js script prints (best-match sims + Fisher J + held-out AUC), rendered
 * with SimilarityBadge / colored cluster-id hashes. Filter boxes: a cluster-id substring, and a
 * per-numeric-column (>,<) threshold (empty = no filter; click the operator to flip > / <).
 */
class FuseboxClusterLinksPage extends LivePage {
  constructor(props) {
    super(props);
    const savedSort = this.getUrlParam('sort');
    const savedPolarity = this.getUrlParam('polarity');
    this.state = {
      ...this.state,
      rows: [],
      setups: [],
      setup: this.getUrlParam('setup') || '',
      scored: 0,
      skipped: 0,
      loading: false,
      error: '',
      clusterFilter: this.getUrlParam('cluster') || '',
      modelIdFilter: this.getUrlParam('modelId') || '',
      polarity: POLARITY_OPTIONS.includes(savedPolarity) ? savedPolarity : '',
      filters: this.filtersFromUrl(),
      sortKey: SORT_VALUE[savedSort] ? savedSort : 'polarity', // default: link/related/reject groups…
      sortDir: this.getUrlParam('dir') === -1 ? -1 : 1, // …ascending (so link first), Fisher-J desc tiebreak
      showAll: false, // table is capped at ROW_LIMIT until expanded
    };
    this.submenu = submenuTests;
    this.svc = this.service('services/fusebox-cluster-links-discriminability');
  }

  /** Numeric-column filters read back from the URL (each {op:'>'|'<', val}); empties stay default. */
  filtersFromUrl() {
    const filters = emptyFilters();
    const saved = this.getUrlParam('filters');
    if (saved && typeof saved === 'object') {
      for (const key of FILTER_KEYS) {
        const f = saved[key];
        if (f && (f.op === '>' || f.op === '<') && typeof f.val === 'string') {
          filters[key] = { op: f.op, val: f.val };
        }
      }
    }
    return filters;
  }

  /** Persist every filter/sort to the URL (only the active ones), so reload/share keeps the view. */
  componentDidUpdate(prevProps, prevState) {
    const watch = ['setup', 'clusterFilter', 'modelIdFilter', 'polarity', 'sortKey', 'sortDir', 'filters'];
    if (watch.some((k) => prevState[k] !== this.state[k])) this.syncUrl();
  }

  syncUrl() {
    const { setup, clusterFilter, modelIdFilter, polarity, sortKey, sortDir, filters } = this.state;
    const params = new URLSearchParams();
    const set = (k, v) => params.set(k, JSON.stringify(v)); // JSON-encoded, matching getUrlParam
    if (setup) set('setup', setup);
    if (clusterFilter) set('cluster', clusterFilter);
    if (modelIdFilter) set('modelId', modelIdFilter);
    if (polarity) set('polarity', polarity);
    set('sort', sortKey);
    set('dir', sortDir);
    const active = {};
    for (const key of FILTER_KEYS) if (filters[key].val !== '') active[key] = filters[key];
    if (Object.keys(active).length) set('filters', active);
    // replace (not push) so live filtering doesn't flood the back button.
    this.props.history.replace({ pathname: this.props.location.pathname, search: `?${params.toString()}` });
  }

  componentDidMount() {
    super.componentDidMount();
    this.loadSetups().then(() => this.run());
  }

  async loadSetups() {
    try {
      const res = await this.svc.find({ query: { setups: '1' } });
      this.setState({ setups: res.setups || [] });
    } catch (err) {
      this.setState({ error: String(err.message || err) });
    }
  }

  async run() {
    this.setState({ loading: true, error: '' });
    try {
      const res = await this.svc.find({ query: { setup: this.state.setup } });
      this.setState({
        rows: res.rows || [],
        scored: res.scored || 0,
        skipped: res.skipped || 0,
        loading: false,
      });
    } catch (err) {
      this.setState({ error: String(err.message || err), loading: false });
    }
  }

  // --- filtering ---------------------------------------------------------------
  toggleOp(key) {
    this.setState((s) => ({
      filters: { ...s.filters, [key]: { ...s.filters[key], op: s.filters[key].op === '>' ? '<' : '>' } },
    }));
  }

  setVal(key, val) {
    this.setState((s) => ({ filters: { ...s.filters, [key]: { ...s.filters[key], val } } }));
  }

  // Click a column name to sort by it; click the active column again to invert the direction.
  sortBy(key) {
    this.setState((s) => (s.sortKey === key
      ? { sortDir: -s.sortDir }
      : { sortKey: key, sortDir: STRING_SORT_COLS.has(key) ? 1 : -1 })); // strings asc, the rest desc
  }

  filteredRows() {
    const { rows, clusterFilter, modelIdFilter, polarity, filters, sortKey, sortDir } = this.state;
    const cf = clusterFilter.trim().toLowerCase();
    const mf = modelIdFilter.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (polarity && r.polarity !== polarity) return false;
      if (cf && !(r.clusterIdA.toLowerCase().includes(cf) || r.clusterIdB.toLowerCase().includes(cf))) {
        return false;
      }
      if (mf && !(r.modelIdA.toLowerCase().includes(mf) || r.modelIdB.toLowerCase().includes(mf))) {
        return false;
      }
      for (const key of FILTER_KEYS) {
        const f = filters[key];
        if (f.val === '') continue; // cleared = no filter on this column
        const t = parseFloat(f.val);
        if (!Number.isFinite(t)) continue;
        const v = FILTER_VALUE[key](r);
        if (v == null) return false; // n/a never passes a numeric filter
        if (f.op === '>' ? !(v > t) : !(v < t)) return false;
      }
      return true;
    });

    const valueOf = SORT_VALUE[sortKey] || SORT_VALUE.polarity;
    out.sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      if (cmp) return sortDir * cmp;
      return (b.fisherJ ?? -Infinity) - (a.fisherJ ?? -Infinity); // stable Fisher-J desc tiebreak
    });
    return out;
  }

  // --- render ------------------------------------------------------------------
  sortLabel(key, label) {
    const active = this.state.sortKey === key;
    const arrow = active ? (this.state.sortDir === 1 ? ' ▲' : ' ▼') : '';
    return (
      <span
        className={active ? 'text-primary' : ''}
        style={{ cursor: 'pointer', userSelect: 'none' }}
        title="Click to sort (click again to invert)"
        onClick={() => this.sortBy(key)}
      >
        {label}{arrow}
      </span>
    );
  }
  renderNumFilter(key) {
    const f = this.state.filters[key];
    return (
      <div className="d-flex align-items-center" style={{ gap: 2 }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary py-0 px-1"
          style={{ width: 24, fontWeight: 'bold', lineHeight: 1.2 }}
          title="Click to switch between greater-than and less-than"
          onClick={() => this.toggleOp(key)}
        >
          {f.op}
        </button>
        <input
          type="number"
          step="any"
          placeholder="—"
          className="form-control form-control-sm py-0"
          style={{ width: 64 }}
          value={f.val}
          onChange={(e) => this.setVal(key, e.target.value)}
        />
      </div>
    );
  }

  /** Cluster-id substring filter (matches either side), shown under the Cluster A header. */
  renderClusterFilter() {
    return (
      <input
        type="text"
        placeholder="id contains…"
        className="form-control form-control-sm"
        style={{ width: 120, fontWeight: 'normal' }}
        value={this.state.clusterFilter}
        onChange={(e) => this.setState({ clusterFilter: e.target.value })}
      />
    );
  }

  renderModelIdFilter() {
    return (
      <ModelIdSelector
        value={this.state.modelIdFilter}
       onChange={m => this.setState({ modelIdFilter: m || '' })}
      />
    );
  }

  /** Polarity selector, shown under the Polarity header. */
  renderPolarityFilter() {
    return (
      <div className="zoom-90" style={{ width: 130, fontWeight: 'normal' }}>
        <SingleValueSelector
          options={POLARITY_OPTIONS}
          value={this.state.polarity || null}
          onChange={(v) => this.setState({ polarity: v || '' })}
          placeholder="all"
          styles={{ menu: (base) => ({ ...base, zIndex: 5 }) }}
        />
      </div>
    );
  }

  renderControls() {
    const { setups, setup, loading, scored, skipped } = this.state;
    const shownCount = this.state.rows.length ? this.filteredRows().length : 0;
    return (
      <div className="border rounded bg-light p-2 mb-3 d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
        <label className="mb-0 small">
          Embedding setup{' '}
          <select
            className="form-control form-control-sm d-inline-block"
            style={{ width: 320 }}
            value={setup}
            onChange={(e) => this.setState({ setup: e.target.value })}
          >
            <option value="">(default setup)</option>
            {setups.map((s) => (
              <option key={s.setupName} value={s.setupName}>
                {s.setupName || '(unnamed)'} — {s.count} imgs
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn btn-sm btn-primary" disabled={loading} onClick={() => this.run()}>
          {loading ? 'Running…' : 'Run'}
        </button>
        <span className="small text-muted ml-auto">
          {loading ? 'computing…' : `${shownCount} / ${scored} match · ${skipped} skipped (too few imgs)`}
        </span>
      </div>
    );
  }

  openScatter(r) {
    this.openModal(<MultiClusterScatterModal clusterIds={[r.clusterIdA, r.clusterIdB]} modelIds={[r.modelIdA, r.modelIdB]} />, {
      title: (
        <span>
          PCA of <BadgeColorHash id={r.clusterIdA} /> and <BadgeColorHash id={r.clusterIdB} />
        </span>
      ),
      fullScreenFixed: true,
      oPadding: true,
    });
  }

  renderRow(r) {
    return (
      <tr key={`${r.clusterIdA}|${r.clusterIdB}|${r.polarity}`}>
        <td>
          <BadgeColorHash id={r.clusterIdA} />
          {r.modelIdA ? <div className="text-muted small text-nowrap">{r.modelIdA}</div> : null}
        </td>
        <td>
          <BadgeColorHash id={r.clusterIdB} />
          {r.modelIdB ? <div className="text-muted small text-nowrap">{r.modelIdB}</div> : null}
        </td>
        <td>
          <span className={`badge ${POL_CLASS[r.polarity] || 'badge-secondary'}`}>{r.polarity}</span>
        </td>
        <td className="text-muted small text-nowrap">{r.nA}+{r.nB}</td>
        <td><SimilarityBadge value={r.median} digits={3} /></td>
        <td><SimilarityBadge value={r.avg} digits={3} /></td>
        <td><SimilarityBadge value={r.best} digits={3} /></td>
        <td><SimilarityBadge value={r.worst} digits={3} /></td>
        <td className="text-right">{r.fisherJ != null ? r.fisherJ.toFixed(2) : '—'}</td>
        <td className={`text-right ${r.auc != null && r.auc > 0.85 ? 'text-success font-weight-bold' : ''}`}>
          {r.auc != null ? r.auc.toFixed(3) : '—'}
        </td>
        <td className="text-nowrap">
          <a
            className="btn btn-sm btn-outline-secondary py-0 px-1"
            target="_blank"
            rel="noreferrer"
            href={clusterPairAdminUrl(r.clusterIdA, r.clusterIdB)}
            title="Open the cluster pair"
          >
            ⧉
          </a>
          <IconButton
            icon="scatter_plot"
            level="secondary"
            className="ml-1"
            title="PCA scatter of both clusters' embeddings"
            onClick={() => this.openScatter(r)}
          />
        </td>
      </tr>
    );
  }

  renderTable() {
    if (this.state.loading) return <div className="alert alert-info">Computing discriminability over all cluster links… (loads all embeddings; first run is the slow one)</div>;
    const rows = this.filteredRows();
    const { showAll } = this.state;
    const shown = showAll ? rows : rows.slice(0, ROW_LIMIT);
    return (
      <>
        <table className="table table-sm table-hover small">
          <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th>
                {this.renderClusterFilter()}
              </th>
              <th>
                {this.renderModelIdFilter()}
              </th>
              <th>
                {this.renderPolarityFilter()}
              </th>
              <th>
                {this.renderNumFilter('imgs')}
              </th>
              {NUM_COLS.map((c) => (
                <th key={c.key} title={c.title}>
                  {this.renderNumFilter(c.key)}
                </th>
              ))}
            </tr>
            <tr>
              <th>
                {this.sortLabel('clusterIdA', 'Cluster A')}
              </th>
              <th>
                {this.sortLabel('clusterIdB', 'Cluster B')}
              </th>
              <th>
                {this.sortLabel('polarity', 'Polarity')}
              </th>
              <th title="embedded images; the filter is on the smaller side (min images per side)">
                {this.sortLabel('imgs', 'imgs')}                
              </th>
              {NUM_COLS.map((c) => (
                <th key={c.key} title={c.title}>
                  {this.sortLabel(c.key, c.label)}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => this.renderRow(r))}
          </tbody>
        </table>
        {rows.length > ROW_LIMIT ? (
          <div className="text-center my-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => this.setState({ showAll: !showAll })}
            >
              {showAll ? `Show first ${ROW_LIMIT}` : `Show all ${rows.length}`}
            </button>
          </div>
        ) : null}
      </>
    );
  }

  renderPageBody() {
    const { error } = this.state;
    return (
      <div className="container-fluid mt-2">
        <h4>Fusebox cluster links — discriminability</h4>
        <div className="small text-muted mb-2">
          One row per <code>image_cluster_links</code> doc: best-match similarities (median / avg / best /
          worst, unit [0,1]) plus the Fisher-J discriminant and held-out AUC between the two clusters'
          embeddings. A <strong>link</strong> asserts "same template", so a high Fisher J / AUC flags a
          suspicious link; a high AUC on a <strong>reject</strong> confirms they're separable.
        </div>
        {error ? <div className="alert alert-danger">{error}</div> : null}
        {this.renderControls()}
        {this.renderTable()}
      </div>
    );
  }
}

FuseboxClusterLinksPage.contextType = LegoAdminPageContext;

export default function FuseboxClusterLinksPageFunc(props) {
  return <FuseboxClusterLinksPage {...props} />;
}